import {
    BuildingInspectionInfo,
    BuildingSubsidenceStatus,
    BuildingConcreteStatus,
    SteelGirdersStatus,
    BuildingCracksAndFissuresStatus,
    BuildingRoofStabilityStatus,
    BuildingFloorStabilityStatus,
    BuildingChimneyStabilityStatus
} from "shared/types/building-inspection";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { FileLogStatus } from "shared/types/file";
import { BuildingDTO } from "shared/types/building";

export const calculateStabilityInfoDotValues = async (buildingInspectionInfo: BuildingInspectionInfo, building: BuildingDTO, session: any) => {
    let dotValues: any = {
        red: { count: 0, labels: [] },
        orange: { count: 0, labels: [] },
        green: { count: 0, labels: [] },
        transparent: { count: 0, labels: [] },
    };

    // Subsidence Status
    if (buildingInspectionInfo.buildingSubsidences === BuildingSubsidenceStatus.OK) {
        dotValues.green.count++;
        dotValues.green.labels.push("Geen verzakking gedetecteerd");
    } else if (buildingInspectionInfo.buildingSubsidences === BuildingSubsidenceStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Verzakking gedetecteerd");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Verzakkingen aanwezig?");
    }

    // Concrete Status
    if ([BuildingConcreteStatus.OK, BuildingConcreteStatus.NOT_REQUIRED].includes(buildingInspectionInfo.buildingConcreteStatus)) {
        dotValues.green.count++;
        dotValues.green.labels.push("Betonconstructie in orde");
    } else if (buildingInspectionInfo.buildingConcreteStatus === BuildingConcreteStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Problemen met betonconstructie");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Betonconstructie aanwezig en ok?");
    }

    // Steel Girders Status
    if (buildingInspectionInfo.steelGirdersStatus === SteelGirdersStatus.OK) {
        dotValues.green.count++;
        dotValues.green.labels.push("Staalconstructie in orde");
    } else if(buildingInspectionInfo.steelGirdersStatus === SteelGirdersStatus.NOT_REQUIRED) {
        dotValues.green.count++;
        dotValues.green.labels.push("Geen staalconstructie, dus geen controle nodig");
    } else if (buildingInspectionInfo.steelGirdersStatus === SteelGirdersStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Problemen met staalconstructie");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Staalconstructie aanwezig en ok?");
    }

    // Cracks and Fissures Status
    if (buildingInspectionInfo.buildingCracksAndFissures === BuildingCracksAndFissuresStatus.OK) {
        dotValues.green.count++;
        dotValues.green.labels.push("Geen scheuren of barsten gedetecteerd");
    } else if (buildingInspectionInfo.buildingCracksAndFissures === BuildingCracksAndFissuresStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Scheuren of barsten gedetecteerd");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Scheuren of barsten gedetecteerd?");
    }

    // Roof Stability Status
    if (buildingInspectionInfo.buildingRoofStabilityStatus === BuildingRoofStabilityStatus.OK) {
        dotValues.green.count++;
        dotValues.green.labels.push("Dakconstructie qua stabiliteit in orde");
    } else if (buildingInspectionInfo.buildingRoofStabilityStatus === BuildingRoofStabilityStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Problemen met stabiliteit van dakconstructie");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Stabiliteit dakconstructie ok?");
    }

    // Floor Stability Status
    if (buildingInspectionInfo.buildingFloorStabilityStatus === BuildingFloorStabilityStatus.OK) {
        dotValues.green.count++;
        dotValues.green.labels.push("Vloerconstructie in orde");
    } else if (buildingInspectionInfo.buildingFloorStabilityStatus === BuildingFloorStabilityStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Problemen met vloerconstructie");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Vloerconstructie ok?");
    }

    // Chimney Stability Status
    if ([BuildingChimneyStabilityStatus.OK].includes(buildingInspectionInfo.buildingChimneyStabilityStatus)) {
        dotValues.green.count++;
        dotValues.green.labels.push("Schoorsteen qua stabiliteit in orde");
    } else if (buildingInspectionInfo.buildingChimneyStabilityStatus === BuildingChimneyStabilityStatus.NOT_REQUIRED) {
        dotValues.green.count++;
        dotValues.green.labels.push("Geen schoorsteen aanwezig");
    } else if (buildingInspectionInfo.buildingChimneyStabilityStatus === BuildingChimneyStabilityStatus.NOT_OK) {
        dotValues.red.count++;
        dotValues.red.labels.push("Problemen met stabiliteit schoorsteen");
    } else {
        dotValues.transparent.count++;
        dotValues.transparent.labels.push("Schoorsteen aanwezig en ok?");
    }

    // Fetch and check stability-related file logs
    try {
        const queryParams = new URLSearchParams();
        if (building?.id) {
            queryParams.append('buildingId', building.id);
        }

        const labels = [
            "Stabiliteit - Stabiliteitsstudie",
            "Stabiliteit - Rapportage plaatsbezoek",
            "Stabiliteit - Communicatie"
        ];
        queryParams.append('labels', labels.join(','));

        const response = await fetchWithAuth(
            `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
            { method: 'GET' },
            session
        );
        const fileLogs = await response.json();
        
        // Process stability study documents
        const stabilityStudyLogs = fileLogs.filter((log: any) => 
            log.label.includes("Stabiliteit - Stabiliteitsstudie")
        ).sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());
        
        if (stabilityStudyLogs.length > 0) {
            const latestStudy = stabilityStudyLogs[0];
            
            if (latestStudy.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("Stabiliteitsstudie uitgevoerd - OK");
            } else if (latestStudy.status === FileLogStatus.OK_WITH_REMARKS) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Stabiliteitsstudie uitgevoerd - met opmerkingen");
            } else if (latestStudy.status === FileLogStatus.NOT_OK) {
                dotValues.red.count++;
                dotValues.red.labels.push("Stabiliteitsstudie uitgevoerd - niet OK");
            } else {
                dotValues.transparent.count++;
                dotValues.transparent.labels.push("Stabiliteitsstudie uitgevoerd - status niet ingevuld");
            }
        }
        
        // Process site visit reports
        const siteVisitLogs = fileLogs.filter((log: any) => 
            log.label.includes("Stabiliteit - Rapportage plaatsbezoek")
        ).sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());
        
        if (siteVisitLogs.length > 0) {
            const latestVisit = siteVisitLogs[0];
            
            if (latestVisit.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("Stabiliteit plaatsbezoek uitgevoerd - OK");
            } else if (latestVisit.status === FileLogStatus.OK_WITH_REMARKS) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Stabiliteit plaatsbezoek uitgevoerd - met opmerkingen");
            } else if (latestVisit.status === FileLogStatus.NOT_OK) {
                dotValues.red.count++;
                dotValues.red.labels.push("Stabiliteit plaatsbezoek uitgevoerd - niet OK");
            }
        }
        
    } catch (error) {
        console.error('Error fetching stability document logs:', error);
    }

    return dotValues;
};
